import * as React from "react";
import classNames from "classnames";

import * as pluralize from "pluralize";

import ReasoningToolPanelDeleteCell from "./Panel/DeleteCell";

import generateUUID from "../../../helpers/generateUUID";

interface ReasoningToolPanelProps {
  title: string;
  infoBox?: React.ReactNode;
  isDeleting?: boolean;
  toggleDeleting?: () => void;
  headerButton?: React.ReactNode;
  isAddable?: boolean;
  wide?: boolean;
  availableOffline?: boolean;
  children: React.ReactNode;
  panelNodeEditStart?: (uid: string) => void;
  addNode?: (uid: string, data?: Record<string, any>) => void;
  deleteNode?: (uid: string) => void;
  displayContext?: DisplayContext;
  returnFocusRef?: React.RefObject<HTMLElement>;
  activePanelType?: string;
  addButtonRef?: React.RefObject<HTMLDivElement>;
  onPanelClose?: () => void; // new prop
  [key: string]: any; // allow data-expanded and other props
}

export default function ReasoningToolPanel({
  title = "",
  infoBox = null,
  isDeleting = false,
  toggleDeleting,
  headerButton = null,
  isAddable = false,
  wide = false,
  availableOffline = false,
  children,
  panelNodeEditStart,
  addNode,
  deleteNode,
  displayContext,
  returnFocusRef,
  activePanelType,
  addButtonRef,
  onPanelClose,
  ...rest
}: ReasoningToolPanelProps) {

  // Ref for the edit icon (delete mode)
  const editIconRef = React.useRef<HTMLDivElement>(null);

  const [isOffline, setIsOffline] = React.useState(!navigator.onLine);

  React.useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  const closePanel = React.useCallback(() => {
    if (displayContext && typeof displayContext.togglePanel === "function") {
      displayContext.togglePanel(activePanelType as any);
    }
    if (typeof onPanelClose === "function") onPanelClose();
  }, [displayContext, activePanelType, onPanelClose]);

  let wrappedChildren = children;
  if (isOffline && !availableOffline) {
    wrappedChildren = (
      <div style={{
        padding: "24px 16px",
        textAlign: "center",
        color: "#555",
        fontFamily: "Roboto, sans-serif",
        fontSize: "15px",
        lineHeight: "1.5",
      }}>
        <p style={{ fontWeight: 700, marginBottom: "8px", color: "#333" }}>
          You are offline
        </p>
        <p>Please reconnect and try again.</p>
        <div className="gen-modal-choice-buttons" style={{ marginTop: "16px" }}>
          <button
            type="button"
            className="aq-button-2 medium with-margin"
            onClick={closePanel}
          >
            OK
          </button>
        </div>
      </div>
    );
  } else if (isDeleting) {
    wrappedChildren = React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        // Try to get uid from props, fallback to key if needed
        const uid = child.props && child.props.uid ? child.props.uid : child.key;
        const deleteFn = deleteNode && uid
          ? () => {
              if (displayContext) {
                displayContext.setFocusedBookmarkUid(null);
              }
              deleteNode(uid);
            }
          : () => {};
        return (
          <ReasoningToolPanelDeleteCell
            key={uid}
            deleteNode={deleteFn}
          >
            {child}
          </ReasoningToolPanelDeleteCell>
        );
      } else {
        return child;
      }
    });
  }

  const panelRef = React.useRef<HTMLDivElement>(null);
  
  // Move focus into panel when it opens
  React.useEffect(() => {
    const panelNode = panelRef.current;
    if (!panelNode) return;
    
    // Track all timeouts so we can clear them on cleanup
    const timeouts: NodeJS.Timeout[] = [];
    
    // Try multiple times with increasing delays to catch dynamically rendered inputs
    const tryFocus = (attempt = 0) => {
      if (attempt > 5) { // Give up after 5 attempts
        // As a fallback, focus the first focusable element in the panel
        const firstFocusable = panelNode.querySelector(
          'button, [role="button"][tabindex="0"], a[href], textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
        ) as HTMLElement;
        if (firstFocusable) {
          firstFocusable.focus();
        }
        return;
      }
      
      // Look for focusable elements (prioritize inputs for editing)
      const focusableSelectors = [
        '.gen-paragraph-input', // Specific class for note inputs
        'textarea:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        '[contenteditable="true"]',
      ];
      
      for (const selector of focusableSelectors) {
        const element = panelNode.querySelector(selector) as HTMLElement;
        if (element && element.offsetParent !== null) { // Check if element is visible
          try {
            element.focus();
            return; // Exit if focus succeeded
          } catch (err) {
            console.error('Failed to focus element:', err);
          }
        }
      }
      
      // Try again with longer delay - track this timeout
      const retryTimeout = setTimeout(() => tryFocus(attempt + 1), 100);
      timeouts.push(retryTimeout);
    };
    
    // Start trying after initial delay
    const initialTimeout = setTimeout(() => tryFocus(0), 100);
    timeouts.push(initialTimeout);
    
    return () => {
      // Clear all timeouts to prevent memory leaks
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []); // Empty dependency array - only run on mount
  
  // Handle keyboard events
  React.useEffect(() => {
    const panelNode = panelRef.current;
    if (!panelNode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle Escape key
      if (e.key === "Escape" && displayContext && typeof displayContext.togglePanel === "function") {
        e.preventDefault();
        displayContext.togglePanel(activePanelType as any);
        // Set data-expanded to false directly on the panel DOM node
        if (panelRef.current) {
          panelRef.current.setAttribute('data-expanded', 'false');
        }
        // Also set data-expanded to false on the sidebar
        const sideNav = document.querySelector('.reasoning-tool-side-nav');
        if (sideNav) {
          sideNav.setAttribute('data-expanded', 'false');
        }
        if (typeof onPanelClose === 'function') onPanelClose();
        // Return focus to the element that triggered the panel
        const triggerElement = (window as any).__lastNotesTriggerElement;
        if (triggerElement && document.contains(triggerElement)) {
          // Focus the element that opened the panel (doc-note-button or toolbar button)
          triggerElement.focus();
          // Clear the stored element
          (window as any).__lastNotesTriggerElement = null;
        } else if (returnFocusRef && returnFocusRef.current) {
          // Fallback to the returnFocusRef if the trigger element is gone
          returnFocusRef.current.focus();
        } else {
          // Fallback: focus the next visible menu button after the one matching the just-closed panel (by name), or wrap to the first visible
          const sidebarButtons = Array.from(document.querySelectorAll('.reasoning-tool-bar-button'));
          const visibleButtons = sidebarButtons.filter(btn => btn instanceof HTMLElement && btn.offsetParent !== null);
          // Try to match by button text (panel title)
          let closedPanelIdx = -1;
          if (typeof activePanelType === 'string') {
            closedPanelIdx = visibleButtons.findIndex(btn => {
              const btnText = btn.textContent?.trim().toLowerCase();
              return btnText === (activePanelType || '').toLowerCase();
            });
          }
          let nextBtn = null;
          if (closedPanelIdx !== -1 && visibleButtons.length > 1) {
            nextBtn = visibleButtons[(closedPanelIdx + 1) % visibleButtons.length];
          } else if (visibleButtons.length > 0) {
            nextBtn = visibleButtons[0];
          }
          if (nextBtn && nextBtn instanceof HTMLElement) {
            nextBtn.focus();
          }
        }
        return;
      }

      // Handle Tab key for focus trapping
      if (e.key === "Tab") {
        const focusableElements = panelNode.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
        );
        const focusableArray = Array.from(focusableElements) as HTMLElement[];
        
        if (focusableArray.length === 0) return;

        const firstElement = focusableArray[0];
        const lastElement = focusableArray[focusableArray.length - 1];

        if (e.shiftKey) {
          // Shift+Tab: If at first element, wrap to last
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab: If at last element, wrap to first
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    panelNode.addEventListener("keydown", handleKeyDown);
    
    return () => {
      panelNode.removeEventListener("keydown", handleKeyDown);
    };
  }, [displayContext, returnFocusRef, activePanelType, onPanelClose]);

  return (
    <div
      className={classNames("reasoning-tool-panel", (wide ? 'wide' : 'small'))}
      ref={panelRef}
      tabIndex={-1}
      {...rest}
    >
      <div className="reasoning-tool-panel-header">
        <div className="reasoning-tool-panel-header-left">
          <h1>{title}</h1>
          {toggleDeleting && (
            <div
              className={classNames("reasoning-tool-panel-header-delete-button", {active: isDeleting})}
              onClick={toggleDeleting}
              role="button"
              tabIndex={0}
              ref={editIconRef}
              aria-label={isDeleting ? "Exit delete mode" : "Enter delete mode"}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleDeleting();
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  // Focus Add button if present
                  addButtonRef?.current && addButtonRef.current.focus();
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  // Focus first diagnosis row if present
                  const rows = document.querySelectorAll('.diagnosis-row-accessible');
                  if (rows.length > 0) {
                    (rows[0] as HTMLElement).focus();
                  } else if (addButtonRef?.current) {
                    // If no rows, stay in menu (focus add button)
                    addButtonRef.current.focus();
                  }
                }
              }}
            >
              <i className="icon-lg icon-edit" aria-hidden="true"></i>
            </div>
          )}
          {headerButton}
        </div>
        <div className="reasoning-tool-panel-header-right">
        </div>
      </div>
      {infoBox && <div className="reasoning-tool-panel-header">
        <p><i>{infoBox}</i></p>
      </div>}
      <div>
        {wrappedChildren}
      </div>
      {isAddable && (!isOffline || availableOffline) && <div 
        className="reasoning-tool-add-button" 
        key="button" 
        role="button"
        tabIndex={0}
        ref={addButtonRef}
        onClick={() => {
          if (addNode && panelNodeEditStart) {
            const newUid = generateUUID();
            addNode(newUid);
            panelNodeEditStart(newUid);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (addNode && panelNodeEditStart) {
              const newUid = generateUUID();
              addNode(newUid);
              panelNodeEditStart(newUid);
            }
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            // Focus last row
            const rows = document.querySelectorAll('.diagnosis-row-accessible');
            if (rows.length > 0) {
              (rows[rows.length - 1] as HTMLElement).focus();
            }
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            // Focus edit icon in header
            editIconRef.current && editIconRef.current.focus();
          }
        }}
      >
          <span><i className="icon-open-box" aria-hidden="true"></i> New {pluralize.singular(title)}...</span>
      </div>}
    </div>
  );
}
